import {
  BarChart3,
  Boxes,
  ChevronLeft,
  ChevronRight,
  FileText,
  Globe2,
  Home,
  Package,
  Settings,
  ShoppingBag,
  Sparkles,
  Store,
  WandSparkles,
} from "lucide-react";
import { NavLink } from "react-router-dom";
import { Brand } from "../ui/Brand";
import { cn } from "../../lib/cn";
import { useAccount } from "../../context/AccountContext";

const navigation = [
  { label: "Beranda", to: "/dashboard", icon: Home, end: true },
  { label: "Website", to: "/dashboard/website", icon: Globe2 },
  { label: "Builder", to: "/dashboard/builder", icon: WandSparkles },
  { label: "Produk", to: "/dashboard/products", icon: Package },
  { label: "Pesanan", to: "/dashboard/orders", icon: ShoppingBag },
  { label: "Analitik", to: "/dashboard/analytics", icon: BarChart3 },
];

const exportNavigation = [
  { label: "Export Center", to: "/dashboard/export", icon: Store, end: true },
  { label: "Katalog Ekspor", to: "/dashboard/export/catalog", icon: FileText, end: false },
  { label: "Buyer Opportunities", to: "/dashboard/export/buyers", icon: Boxes, end: false },
];

type DashboardSidebarProps = {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
};

export function DashboardSidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: DashboardSidebarProps) {
  const { account } = useAccount();
  const showExport = Boolean(account?.entitlements.exportTools);

  return (
    <>
      {mobileOpen && <button type="button" aria-label="Tutup menu" onClick={onMobileClose} className="fixed inset-0 z-40 bg-slate-950/45 backdrop-blur-sm lg:hidden" />}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex flex-col overflow-hidden border-r border-white/10 bg-emerald-950 text-white shadow-[18px_0_70px_rgba(6,45,28,.16)] transition-all duration-200",
          collapsed ? "w-[88px]" : "w-[272px]",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        )}
      >
        <div className="pointer-events-none absolute -left-20 top-32 h-64 w-64 rounded-full bg-brand-500/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -right-20 h-64 w-64 rounded-full bg-accent-500/15 blur-3xl" />

        <div className={cn("relative flex h-20 items-center border-b border-white/10 px-5", collapsed ? "justify-center" : "justify-between") }>
          <Brand compact={collapsed} inverse />
          {!collapsed && (
            <button type="button" onClick={onToggle} className="hidden h-9 w-9 place-items-center rounded-xl border border-white/10 bg-white/5 text-white/60 hover:bg-white/10 hover:text-white lg:grid" aria-label="Kecilkan sidebar">
              <ChevronLeft size={18} />
            </button>
          )}
        </div>

        {collapsed && (
          <button type="button" onClick={onToggle} className="relative mx-auto mt-4 hidden h-9 w-9 place-items-center rounded-xl border border-white/10 bg-white/5 text-white/60 hover:bg-white/10 hover:text-white lg:grid" aria-label="Besarkan sidebar">
            <ChevronRight size={18} />
          </button>
        )}

        <div className="hide-scrollbar relative flex-1 overflow-y-auto px-3 py-5">
          <p className={cn("mb-2 px-3 text-[10px] font-black uppercase tracking-[0.18em] text-white/[.35]", collapsed && "sr-only")}>Workspace</p>
          <nav className="space-y-1.5">
            {navigation.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onMobileClose}
                className={({ isActive }: { isActive: boolean }) =>
                  cn(
                    "group flex items-center rounded-2xl px-3 py-3 text-sm font-bold transition",
                    collapsed ? "justify-center" : "gap-3",
                    isActive ? "bg-white text-emerald-950 shadow-lg" : "text-white/[.58] hover:bg-white/[.08] hover:text-white",
                  )
                }
                title={collapsed ? item.label : undefined}
              >
                <item.icon size={19} strokeWidth={2.25} />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
          </nav>

          {showExport && (<>
            <div className="my-5 border-t border-white/10" />
            <div className={cn("mb-3 flex items-center justify-between px-3", collapsed && "justify-center") }>
              {!collapsed && <p className="text-[10px] font-black uppercase tracking-[0.18em] text-white/[.35]">Export Tools</p>}
              <span className="rounded-full bg-accent-500 px-2 py-0.5 text-[9px] font-black text-white">EXPORT</span>
            </div>
            <nav className="space-y-1.5">
              {exportNavigation.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onMobileClose}
                  className={({ isActive }: { isActive: boolean }) => cn("flex items-center rounded-2xl px-3 py-3 text-sm font-bold", collapsed ? "justify-center" : "gap-3", isActive ? "bg-accent-500 text-white" : "text-white/[.58] hover:bg-white/[.08] hover:text-white")}
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon size={19} strokeWidth={2.25} />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              ))}
            </nav>
          </>)}
        </div>

        <div className="relative border-t border-white/10 p-3">
          {!collapsed && (
            <div className="mb-3 overflow-hidden rounded-2xl border border-white/10 bg-white/[.06] p-4">
              <div className="flex items-center gap-2 text-[10px] font-extrabold uppercase tracking-[.16em] text-brand-200"><Sparkles size={14} /> Paket aktif</div>
              <p className="mt-2 text-lg font-black">{account?.planLabel}</p>
              <p className="mt-1 text-xs leading-5 text-white/50">Satu akun untuk WhatsApp, website, dan dashboard.</p>
            </div>
          )}
          <NavLink
            to="/dashboard/settings"
            className={({ isActive }: { isActive: boolean }) => cn("flex items-center rounded-2xl px-3 py-3 text-sm font-bold", collapsed ? "justify-center" : "gap-3", isActive ? "bg-white text-emerald-950" : "text-white/[.58] hover:bg-white/[.08] hover:text-white")}
            title={collapsed ? "Pengaturan" : undefined}
          >
            <Settings size={19} />
            {!collapsed && <span>Pengaturan</span>}
          </NavLink>
        </div>
      </aside>
    </>
  );
}
